import { useEffect, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { DataTable, Column } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { FormField } from '@/components/FormField';
import { tiktokService, TiktokAdGroup, TiktokCampaign, TiktokPixelOption } from '@/services/tiktok.service';
import { LastSyncedBadge } from '@/components/LastSyncedBadge';
import { Layers, RefreshCw, Power, ExternalLink, Plus, MapPin, Loader2, Send, AlertTriangle } from 'lucide-react';
import { TIKTOK_COUNTRIES } from '@/components/tiktok/workspace/tiktokGeo';
import {
  DEFAULT_FREQUENCY_CAP, defaultGoalFor, goalSpec, goalsForObjective, objectiveSpec,
  type TiktokBidType,
} from '@/lib/tiktok-matrix';
import clsx from 'clsx';
import toast from 'react-hot-toast';

interface TiktokRegion { locationId: string; name: string; regionCode?: string }

type BidType = TiktokBidType;
type AdGroupBudgetMode = 'BUDGET_MODE_DAY' | 'BUDGET_MODE_TOTAL';
type ScheduleType = 'SCHEDULE_FROM_NOW' | 'SCHEDULE_START_END';

const NO_EVENTS_WARNING =
  'This pixel has no events configured yet. Create the event in TikTok Events Manager, then reload.';

const PLACEMENT_OPTIONS: Array<{ value: string; label: string; hint: string }> = [
  { value: 'PLACEMENT_TIKTOK',            label: 'TikTok',            hint: 'In-feed video — the default surface.' },
  { value: 'PLACEMENT_PANGLE',            label: 'Pangle',            hint: 'TikTok\'s third-party publisher network.' },
  { value: 'PLACEMENT_GLOBAL_APP_BUNDLE', label: 'Global App Bundle', hint: 'Cross-publisher placements — broadest reach.' },
];

// Optimisation goals are NOT a fixed list — they come from the parent
// campaign's objective via @/lib/tiktok-matrix.
const BID_TYPES: Array<{ value: BidType; label: string; hint: string }> = [
  { value: 'BID_TYPE_NO_BID', label: 'Auto bid',              hint: 'TikTok sets the bid to spend the budget efficiently.' },
  { value: 'BID_TYPE_CUSTOM', label: 'Manual bid (cost cap)', hint: 'You set a target cost-per-result.' },
];

const ADGROUP_BUDGET_MODES: Array<{ value: AdGroupBudgetMode; label: string }> = [
  { value: 'BUDGET_MODE_DAY',   label: 'Daily' },
  { value: 'BUDGET_MODE_TOTAL', label: 'Lifetime' },
];

const inputCls =
  'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';

const STATUS_COLORS: Record<string, string> = {
  ENABLE: 'bg-success-soft text-success',
  DISABLE: 'bg-surface-alt text-text-muted',
};

/**
 * Format a Date for `<input type="datetime-local">` — yields `YYYY-MM-DDTHH:mm`
 * in the *local* timezone. We send the value back to the backend as ISO; TikTok
 * accepts ISO 8601 in the advertiser's timezone, which lines up.
 */
function dtLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type Props = {
  /** Rendered inside TiktokAdsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
  /** When set, only ad groups belonging to this campaign are listed. */
  campaignId?: string;
  onDrillToAds?: (tiktokAdGroupId: string) => void;
};

export default function TiktokAdGroups({ embedded = false, campaignId, onDrillToAds }: Props = {}) {
  const [groups, setGroups] = useState<TiktokAdGroup[] | null>(null);
  const [campaigns, setCampaigns] = useState<TiktokCampaign[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // ── Create-ad-group modal state ─────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [regions, setRegions] = useState<TiktokRegion[] | null>(null);
  const [regionFilter, setRegionFilter] = useState('');
  const [pixels, setPixels] = useState<TiktokPixelOption[] | null>(null);
  const [pixelsLoading, setPixelsLoading] = useState(false);

  interface CreateForm {
    tiktokCampaignId: string;
    adGroupName: string;
    placements: string[];
    locationIds: string[];
    optimizationGoal: string;
    bidType: BidType;
    /** Sent as bidPrice or conversionBidPrice depending on the goal's manualBidField. */
    bidPrice: string;
    pixelId: string;
    optimizationEvent: string;
    frequency: string;
    frequencySchedule: string;
    budgetMode: AdGroupBudgetMode;
    budget: string;
    scheduleType: ScheduleType;
    scheduleStartTime: string;
    scheduleEndTime: string;
    operationStatus: 'ENABLE' | 'DISABLE';
  }

  const blankForm = (tiktokCampaignId: string, objective?: string): CreateForm => {
    const now = new Date();
    const inAnHour = new Date(now.getTime() + 60 * 60 * 1000);
    const inAWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return {
      tiktokCampaignId,
      adGroupName: '',
      placements: ['PLACEMENT_TIKTOK'],
      locationIds: [], // populated from /regions once the campaign is known
      optimizationGoal: defaultGoalFor(objective),
      bidType: 'BID_TYPE_NO_BID',
      bidPrice: '',
      pixelId: '',
      optimizationEvent: '',
      frequency: String(DEFAULT_FREQUENCY_CAP.frequency),
      frequencySchedule: String(DEFAULT_FREQUENCY_CAP.frequencySchedule),
      budgetMode: 'BUDGET_MODE_DAY',
      budget: '',
      scheduleType: 'SCHEDULE_FROM_NOW',
      scheduleStartTime: dtLocal(inAnHour),
      scheduleEndTime: dtLocal(inAWeek),
      operationStatus: 'DISABLE',
    };
  };

  const [form, setForm] = useState<CreateForm>(() => blankForm(''));

  const resetForm = () => {
    const first = campaigns[0];
    setForm(blankForm(first?.tiktokCampaignId ?? '', first?.objectiveType));
  };

  // ── Matrix-driven optimisation, keyed on the campaign the user picked ────
  const selectedCampaign = campaigns.find((c) => c.tiktokCampaignId === form.tiktokCampaignId) ?? null;
  const objectiveType = selectedCampaign?.objectiveType;
  const objSpec = objectiveSpec(objectiveType);
  const goals = goalsForObjective(objectiveType);
  const selectedGoal = goalSpec(objectiveType, form.optimizationGoal);
  const needsPixel = selectedGoal?.requires.includes('PIXEL_EVENT') ?? false;
  const needsFrequency = selectedGoal?.requires.includes('FREQUENCY_CAP') ?? false;
  const manualBidIsConversion = selectedGoal?.manualBidField === 'conversion_bid_price';
  const manualBidLabel = manualBidIsConversion
    ? 'Cost per conversion'
    : 'Bid price (cost per click/view/1k impressions)';
  const selectedPixel = (pixels ?? []).find((p) => p.pixelId === form.pixelId) ?? null;
  const pixelHasNoEvents = !!selectedPixel && selectedPixel.events.length === 0;

  // Switching campaign can invalidate the chosen goal — snap it back to the
  // new objective's default and drop the pixel selection that went with it.
  useEffect(() => {
    if (!createOpen) return;
    setForm((f) => (goalSpec(objectiveType, f.optimizationGoal)
      ? f
      : { ...f, optimizationGoal: defaultGoalFor(objectiveType), pixelId: '', optimizationEvent: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen, objectiveType]);

  // Countries are per-advertiser, per-objective AND per-placement (live-
  // verified: TikTok 13 countries, Pangle 10, Global App Bundle 9), so refetch
  // whenever any of those change and offer only what ALL selected placements
  // allow. Fallback to the curated list when TikTok returns empty/errors — it
  // re-validates location_ids at create time.
  const placementKey = [...(form.placements.length ? form.placements : ['PLACEMENT_TIKTOK'])].sort().join(',');
  useEffect(() => {
    if (!createOpen) return;
    let cancelled = false;
    const fallback: TiktokRegion[] = TIKTOK_COUNTRIES.map((c) => ({
      locationId: c.id, name: c.label, regionCode: c.code,
    }));
    setRegions(null);
    Promise.all(placementKey.split(',').filter(Boolean).map((placement) =>
      tiktokService.listRegions({ objectiveType: objectiveType || 'TRAFFIC', placement })))
      .then((lists): TiktokRegion[] => {
        const [first, ...rest] = lists;
        const inter = (first ?? []).filter((r) => rest.every((l) => l.some((x) => x.locationId === r.locationId)));
        return inter.length > 0 ? inter : fallback;
      })
      .catch((): TiktokRegion[] => fallback)
      .then((list) => {
        if (cancelled) return;
        setRegions(list);
        const valid = new Set(list.map((x) => x.locationId));
        setForm((f) => {
          const pruned = f.locationIds.filter((id) => valid.has(id));
          if (pruned.length > 0) {
            return pruned.length === f.locationIds.length ? f : { ...f, locationIds: pruned };
          }
          const seed = list.find((x) => x.regionCode === 'AE') ?? list[0];
          return seed ? { ...f, locationIds: [seed.locationId] } : f;
        });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen, objectiveType, placementKey]);

  // Pixels are only needed by PIXEL_EVENT goals — fetch lazily.
  useEffect(() => {
    if (!needsPixel || pixels !== null || pixelsLoading) return;
    setPixelsLoading(true);
    tiktokService.listPixelOptions()
      .then(setPixels)
      .catch(() => setPixels([])) // axios interceptor already toasted
      .finally(() => setPixelsLoading(false));
  }, [needsPixel, pixels, pixelsLoading]);

  const toggleLocation = (id: string) => {
    setForm((f) => ({
      ...f,
      locationIds: f.locationIds.includes(id)
        ? f.locationIds.filter((x) => x !== id)
        : [...f.locationIds, id],
    }));
  };

  // Pull the campaign list when the modal opens so the dropdown is up to date.
  // Cheap — usually a small list, served from local cache. Countries + the
  // valid optimisation-goal set follow the selected campaign's objective and
  // are resolved by the effects above.
  const openCreate = async () => {
    let cs: TiktokCampaign[];
    try {
      cs = await tiktokService.listCampaigns();
      setCampaigns(cs);
      if (cs.length === 0) {
        toast.error('Create a campaign first — ad groups attach to one.');
        return;
      }
      setForm((f) => {
        const chosen = cs.find((c) => c.tiktokCampaignId === f.tiktokCampaignId) ?? cs[0];
        return {
          ...f,
          tiktokCampaignId: chosen.tiktokCampaignId,
          optimizationGoal: goalSpec(chosen.objectiveType, f.optimizationGoal)
            ? f.optimizationGoal
            : defaultGoalFor(chosen.objectiveType),
        };
      });
    } catch {
      toast.error('Could not load campaigns');
      return;
    }
    setCreateOpen(true);
  };

  const togglePlacement = (value: string) => {
    setForm((f) => ({
      ...f,
      placements: f.placements.includes(value)
        ? f.placements.filter((p) => p !== value)
        : [...f.placements, value],
    }));
  };

  const submitCreate = async () => {
    if (!form.tiktokCampaignId) { toast.error('Pick a campaign'); return; }
    if (!form.adGroupName.trim()) { toast.error('Ad group name is required'); return; }
    if (form.placements.length === 0) { toast.error('Pick at least one placement'); return; }
    if (form.locationIds.length === 0) { toast.error('Pick at least one country — TikTok requires geo targeting'); return; }
    if (objSpec?.unavailableReason) {
      toast.error(`TikTok can't run ${objSpec.label} ad groups on this advertiser. ${objSpec.unavailableReason}`);
      return;
    }
    if (!selectedGoal) {
      const allowed = goals.map((g) => g.label).join(', ') || 'none';
      toast.error(`"${form.optimizationGoal}" isn't a valid optimisation goal for a ${objSpec?.label ?? objectiveType ?? 'this'} campaign. TikTok allows: ${allowed}.`);
      return;
    }
    const budgetNum = Number(form.budget);
    if (!Number.isFinite(budgetNum) || budgetNum <= 0) { toast.error('Enter a positive budget'); return; }
    const isCustomBid = form.bidType === 'BID_TYPE_CUSTOM';
    const bidPriceNum = isCustomBid ? Number(form.bidPrice) : undefined;
    if (isCustomBid && (!Number.isFinite(bidPriceNum!) || bidPriceNum! <= 0)) {
      toast.error(manualBidIsConversion
        ? 'Enter a positive cost per conversion for the manual bid'
        : 'Enter a positive bid price for the manual bid');
      return;
    }

    // Extra inputs the matrix says this goal needs.
    if (needsPixel) {
      if (!form.pixelId) { toast.error(`The "${selectedGoal.label}" goal optimises against a pixel event — pick a pixel`); return; }
      if (pixelHasNoEvents) { toast.error(NO_EVENTS_WARNING); return; }
      if (!form.optimizationEvent) { toast.error('Pick the pixel event to optimise for'); return; }
    }
    let frequencyNum: number | undefined;
    let frequencyScheduleNum: number | undefined;
    if (needsFrequency) {
      frequencyNum = Number(form.frequency);
      frequencyScheduleNum = Number(form.frequencySchedule);
      if (!Number.isFinite(frequencyNum) || frequencyNum < 1) {
        toast.error('Frequency cap: show the ad at most 1 or more times'); return;
      }
      if (!Number.isFinite(frequencyScheduleNum) || frequencyScheduleNum < 1) {
        toast.error('Frequency cap: the period must be at least 1 day'); return;
      }
    }
    if (!form.scheduleStartTime) { toast.error('Pick a start time'); return; }
    if (form.scheduleType === 'SCHEDULE_START_END') {
      if (!form.scheduleEndTime) {
        toast.error('Pick an end time, or switch to "Run continuously"'); return;
      }
      if (new Date(form.scheduleEndTime) <= new Date(form.scheduleStartTime)) {
        toast.error('End time must be after the start time'); return;
      }
    }

    setCreating(true);
    try {
      const r = await tiktokService.createAdGroup({
        tiktokCampaignId: form.tiktokCampaignId,
        adGroupName: form.adGroupName.trim(),
        placements: form.placements,
        locationIds: form.locationIds,
        optimizationGoal: form.optimizationGoal,
        bidType: form.bidType,
        // The matrix decides which price field TikTok reads for this goal.
        bidPrice: manualBidIsConversion ? undefined : bidPriceNum,
        conversionBidPrice: manualBidIsConversion ? bidPriceNum : undefined,
        pixelId: needsPixel ? form.pixelId : undefined,
        optimizationEvent: needsPixel ? form.optimizationEvent : undefined,
        frequency: frequencyNum,
        frequencySchedule: frequencyScheduleNum,
        budgetMode: form.budgetMode,
        budget: budgetNum,
        scheduleType: form.scheduleType,
        scheduleStartTime: new Date(form.scheduleStartTime).toISOString(),
        scheduleEndTime: form.scheduleType === 'SCHEDULE_START_END'
          ? new Date(form.scheduleEndTime).toISOString()
          : undefined,
        operationStatus: form.operationStatus,
      });
      toast.success(`Ad group created · TikTok id ${r.adGroupId}`);
      setCreateOpen(false);
      resetForm();
      await silentSync();
      await refresh();
    } catch {
      // axios interceptor toasts the backend message
    } finally { setCreating(false); }
  };

  const refresh = () =>
    tiktokService
      .listAdGroups(campaignId ? { campaignId } : undefined)
      .then(setGroups)
      .catch(() => setGroups([]));

  // Auto-sync on mount when data is empty or 10+ min stale.
  useEffect(() => {
    refresh().then(() => {
      tiktokService.listAdGroups()
        .then((rows) => {
          const newest = rows
            .map((r) => Date.parse(r.lastSyncedAt ?? ''))
            .filter((n) => Number.isFinite(n))
            .reduce((a, b) => Math.max(a, b), 0);
          const stale = !newest || Date.now() - newest > 10 * 60 * 1000;
          if (rows.length === 0 || stale) silentSync();
        })
        .catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const silentSync = async () => {
    try {
      await tiktokService.syncAdGroups();
      await refresh();
    } catch { /* user can retry via the manual button */ }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await tiktokService.syncAdGroups();
      toast.success(`Synced ${r.total} ad groups — ${r.created} new, ${r.updated} updated`);
      await refresh();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setSyncing(false); }
  };

  const toggleStatus = async (g: TiktokAdGroup) => {
    setBusyId(g.tiktokAdGroupId);
    const next = g.operationStatus === 'ENABLE' ? 'DISABLE' : 'ENABLE';
    try {
      await tiktokService.setAdGroupStatus(g.tiktokAdGroupId, next);
      toast.success(`Ad group ${next === 'ENABLE' ? 'enabled' : 'disabled'}`);
      await refresh();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setBusyId(null); }
  };

  const columns: Column<TiktokAdGroup>[] = [
    {
      key: 'name',
      header: 'Ad group',
      width: 'minmax(210px, 2.2fr)',
      render: (g) => (
        <div className="flex items-center gap-2.5">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-text-primary">
            <Layers size={13} />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium text-text-primary">{g.adGroupName}</span>
            <span className="block truncate font-mono text-2xs text-text-muted">{g.tiktokAdGroupId}</span>
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'minmax(88px, 0.6fr)',
      render: (g) => g.operationStatus ? (
        <span className={clsx('inline-block rounded-full px-2 py-0.5 text-2xs font-semibold', STATUS_COLORS[g.operationStatus] ?? 'bg-surface-alt text-text-muted')}>
          {g.operationStatus}
        </span>
      ) : <span className="text-text-muted">—</span>,
    },
    {
      key: 'goal',
      header: 'Optimisation',
      width: 'minmax(120px, 0.9fr)',
      render: (g) => g.optimizationGoal ? (
        <span className="inline-block rounded-full bg-info-soft px-2 py-0.5 text-2xs text-info">{g.optimizationGoal}</span>
      ) : <span className="text-text-muted">—</span>,
    },
    {
      key: 'placements',
      header: 'Placements',
      width: 'minmax(110px, 0.9fr)',
      render: (g) => {
        const p = g.placements ?? [];
        if (p.length === 0) return <span className="text-text-muted">—</span>;
        // Strip the PLACEMENT_ prefix — the enum is noise in a narrow column.
        const short = p.map((x) => x.replace(/^PLACEMENT_/, ''));
        return (
          <span className="truncate text-xs text-text-secondary" title={p.join(', ')}>
            {short.join(', ')}
          </span>
        );
      },
    },
    {
      key: 'budget',
      header: 'Budget',
      width: 'minmax(110px, 0.8fr)',
      render: (g) => g.budget != null ? (
        <span className="tabular-nums text-text-primary">
          {g.budget.toLocaleString()}
          <span className="ml-1 text-2xs text-text-muted">{BUDGET_MODE_LABEL[g.budgetMode ?? ''] ?? g.budgetMode ?? ''}</span>
        </span>
      ) : <span className="text-text-muted">—</span>,
    },
    {
      key: 'bid',
      header: 'Bid',
      width: 'minmax(70px, 0.5fr)',
      render: (g) => g.bidPrice != null
        ? <span className="tabular-nums text-text-secondary">{g.bidPrice}</span>
        : <span className="text-text-muted">—</span>,
    },
    {
      key: 'actions',
      header: '',
      width: 'minmax(160px, auto)',
      render: (g) => (
        <div className="flex items-center justify-end gap-2">
          {onDrillToAds && (
            <button
              onClick={() => onDrillToAds(g.tiktokAdGroupId)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-primary hover:text-primary"
            >
              <Send size={11} /> Ads
            </button>
          )}
          <button
            onClick={() => toggleStatus(g)}
            disabled={busyId === g.tiktokAdGroupId}
            className={clsx(
              'inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
              g.operationStatus === 'ENABLE'
                ? 'border-success/30 bg-success-soft text-success hover:bg-success-soft/70'
                : 'border-border bg-surface-alt text-text-muted hover:bg-border',
              busyId === g.tiktokAdGroupId && 'cursor-wait opacity-60',
            )}
          >
            <Power size={11} /> {g.operationStatus === 'ENABLE' ? 'Pause' : 'Enable'}
          </button>
        </div>
      ),
    },
  ];

  const actions = (
    <div className="flex items-center gap-2">
      <LastSyncedBadge timestamps={(groups ?? []).map(g => g.lastSyncedAt)} />
      <Button variant="secondary" onClick={sync} loading={syncing}><RefreshCw size={14} /> Sync</Button>
      <Button variant="primary" onClick={openCreate}><Plus size={14} /> New ad group</Button>
    </div>
  );

  return (
    <>
      {embedded ? (
        <div className="flex justify-end px-7 pt-4">{actions}</div>
      ) : (
        <Topbar
          title="TikTok Ad Groups"
          breadcrumb="TikTok Ads"
          description="Cached snapshot of ad groups under your default advertiser. Sync to refresh; toggle to enable/disable."
          right={actions}
        />
      )}

      <div className={clsx('w-full space-y-4 px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
        {groups === null && <Card><div className="skeleton h-24 rounded-lg" /></Card>}

        {groups && groups.length === 0 && (
          <EmptyState
            icon={<Layers size={40} className="text-text-muted" />}
            title="No ad groups yet"
            description="Ad groups live inside campaigns. Create one here, or build it in TikTok Ads Manager and Sync."
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button variant="primary" onClick={openCreate}><Plus size={14} /> New ad group</Button>
                <Button variant="secondary" onClick={sync} loading={syncing}><RefreshCw size={14} /> Sync now</Button>
                <a href="https://ads.tiktok.com/i18n/dashboard/campaign" target="_blank" rel="noreferrer">
                  <Button variant="ghost"><ExternalLink size={14} /> Open Ads Manager</Button>
                </a>
              </div>
            }
          />
        )}

        {groups && groups.length > 0 && (
          <DataTable rows={groups} columns={columns} stickyHeader />
        )}
      </div>

      {/* ── Create ad group modal ─────────────────────────────────────────── */}
      <Modal
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        title="New TikTok ad group"
        widthClass="max-w-2xl"
      >
        <div className="space-y-4">
          <p className="rounded-lg border border-border bg-surface-alt/50 p-3 text-xs text-text-secondary">
            Ad groups define <strong>where</strong> the ad shows (placements), <strong>how</strong> TikTok optimises (goal +
            bid), <strong>how much</strong> to spend (budget), and <strong>when</strong> to run (schedule). The group
            starts paused — switch it on after you've added at least one ad.
          </p>

          {objSpec?.unavailableReason && (
            <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-2xs text-warning">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              TikTok can&apos;t run {objSpec.label} ad groups on this advertiser. {objSpec.unavailableReason}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label="Campaign"
              required
              hintBelow
              hint="The campaign's objective decides which optimisation goals and countries TikTok allows."
            >
              <select
                className={inputCls}
                value={form.tiktokCampaignId}
                onChange={(e) => setForm((f) => ({ ...f, tiktokCampaignId: e.target.value }))}
              >
                {campaigns.map((c) => (
                  <option key={c.tiktokCampaignId} value={c.tiktokCampaignId}>
                    {c.campaignName} ({c.objectiveType ?? '—'})
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Ad group name" required>
              <input
                className={inputCls}
                placeholder="e.g. UAE · 18–34 · Auto bid"
                value={form.adGroupName}
                onChange={(e) => setForm((f) => ({ ...f, adGroupName: e.target.value }))}
              />
            </FormField>
          </div>

          <FormField label="Countries" required hintBelow hint="Loaded from TikTok — only countries valid for your advertiser are shown.">
            {regions === null ? (
              <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-alt/40 p-3 text-xs text-text-muted">
                <Loader2 size={12} className="animate-spin" /> Loading countries from TikTok…
              </div>
            ) : regions.length === 0 ? (
              <div className="rounded-xl border border-warning/40 bg-warning-soft p-3 text-xs text-warning">
                TikTok returned no available countries for this advertiser. Confirm the advertiser has a payment
                method on file in TikTok Ads Manager, identity verification is complete, and that the TikTok app
                grants the <code className="font-mono">Ads Management</code> scope. Then disconnect and reconnect
                this advertiser.
              </div>
            ) : (
              <>
                <input
                  value={regionFilter}
                  onChange={(e) => setRegionFilter(e.target.value)}
                  placeholder="Filter…"
                  className={clsx(inputCls, 'mb-2 !py-1.5 text-xs')}
                />
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4 max-h-56 overflow-y-auto rounded-xl border border-border bg-surface-alt/40 p-2">
                  {regions
                    .filter((r) => {
                      if (!regionFilter.trim()) return true;
                      const q = regionFilter.trim().toLowerCase();
                      return r.name.toLowerCase().includes(q) || (r.regionCode ?? '').toLowerCase().includes(q);
                    })
                    .map((r) => {
                      const active = form.locationIds.includes(r.locationId);
                      return (
                        <button
                          type="button"
                          key={r.locationId}
                          onClick={() => toggleLocation(r.locationId)}
                          className={clsx(
                            'rounded-lg border px-2 py-1.5 text-left text-xs transition-colors',
                            active
                              ? 'border-primary bg-primary-soft text-primary'
                              : 'border-border bg-surface text-text-primary hover:border-primary/40',
                          )}
                        >
                          <div className="font-semibold truncate">{r.name}</div>
                          {r.regionCode && <div className="mt-0.5 text-2xs text-text-muted font-mono">{r.regionCode}</div>}
                        </button>
                      );
                    })}
                </div>
                <p className="mt-1 text-2xs text-text-muted">
                  <MapPin size={10} className="inline mr-0.5" />
                  {form.locationIds.length} of {regions.length} {form.locationIds.length === 1 ? 'country' : 'countries'} selected
                </p>
              </>
            )}
          </FormField>

          <FormField label="Placements" required hintBelow hint="Where the ad surfaces. TikTok feed is the most reliable placement.">
            <div className="grid gap-2 sm:grid-cols-3">
              {PLACEMENT_OPTIONS.map((p) => {
                const active = form.placements.includes(p.value);
                return (
                  <button
                    type="button"
                    key={p.value}
                    onClick={() => togglePlacement(p.value)}
                    className={clsx(
                      'rounded-xl border px-3 py-2 text-left text-sm transition-colors',
                      active
                        ? 'border-primary bg-primary-soft text-primary'
                        : 'border-border bg-surface text-text-primary hover:border-primary/40',
                    )}
                  >
                    <div className="font-semibold">{p.label}</div>
                    <div className="mt-0.5 text-2xs text-text-muted">{p.hint}</div>
                  </button>
                );
              })}
            </div>
          </FormField>

          {/* Two-column layout for these picker fields. Their hints are long
              enough to collide with the next column's right-aligned hint slot,
              so we render the hint below the select instead of in the label. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Optimisation goal" required>
              <select
                className={inputCls}
                value={form.optimizationGoal}
                onChange={(e) => setForm((f) => ({
                  ...f, optimizationGoal: e.target.value, pixelId: '', optimizationEvent: '',
                }))}
                disabled={goals.length === 0}
              >
                {goals.length === 0 && <option value="">— unknown campaign objective —</option>}
                {goals.map((g) => <option key={g.goal} value={g.goal}>{g.label}</option>)}
              </select>
              <p className="mt-1 text-2xs text-text-muted">
                {selectedGoal
                  ? `${selectedGoal.hint} TikTok bills this goal as ${selectedGoal.billingEvent}.`
                  : 'Goals are limited to what TikTok accepts for this campaign\'s objective.'}
              </p>
            </FormField>

            <FormField label="Bid type" required>
              <select
                className={inputCls}
                value={form.bidType}
                onChange={(e) => setForm((f) => ({ ...f, bidType: e.target.value as BidType }))}
              >
                {BID_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                <option value="BID_TYPE_MAX_CONVERSION" disabled>
                  Max conversion — not supported by TikTok for this advertiser
                </option>
              </select>
              <p className="mt-1 text-2xs text-text-muted">
                {BID_TYPES.find((b) => b.value === form.bidType)?.hint}
              </p>
            </FormField>
          </div>

          {form.bidType === 'BID_TYPE_CUSTOM' && (
            <FormField
              label={manualBidLabel}
              required
              hintBelow
              hint={manualBidIsConversion
                ? 'Target cost for one conversion, in your advertiser currency.'
                : 'Target cost per result, in your advertiser currency.'}
            >
              <input
                className={inputCls}
                type="number"
                min={0}
                step="any"
                placeholder="0.50"
                value={form.bidPrice}
                onChange={(e) => setForm((f) => ({ ...f, bidPrice: e.target.value }))}
              />
            </FormField>
          )}

          {needsFrequency && (
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Show ad at most" required hintBelow hint="TikTok rejects reach ad groups without a cap.">
                <div className="relative">
                  <input
                    className={inputCls}
                    type="number"
                    min={1}
                    step={1}
                    value={form.frequency}
                    onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-2xs text-text-muted">
                    times
                  </span>
                </div>
              </FormField>
              <FormField
                label="every"
                required
                hintBelow
                hint={`Default is ${DEFAULT_FREQUENCY_CAP.frequency} per ${DEFAULT_FREQUENCY_CAP.frequencySchedule} days.`}
              >
                <div className="relative">
                  <input
                    className={inputCls}
                    type="number"
                    min={1}
                    step={1}
                    value={form.frequencySchedule}
                    onChange={(e) => setForm((f) => ({ ...f, frequencySchedule: e.target.value }))}
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-2xs text-text-muted">
                    days
                  </span>
                </div>
              </FormField>
            </div>
          )}

          {needsPixel && (
            <div className="grid gap-3 sm:grid-cols-2">
              <FormField label="Pixel" required hintBelow hint="TikTok optimises against events on this pixel.">
                {pixelsLoading || pixels === null ? (
                  <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-alt/40 p-3 text-xs text-text-muted">
                    <Loader2 size={12} className="animate-spin" /> Loading pixels…
                  </div>
                ) : pixels.length === 0 ? (
                  <div className="rounded-xl border border-warning/40 bg-warning-soft p-3 text-2xs text-warning">
                    No pixels on this advertiser yet. Create one under TikTok → Pixel, then reload.
                  </div>
                ) : (
                  <select
                    className={inputCls}
                    value={form.pixelId}
                    onChange={(e) => setForm((f) => ({
                      // The event list is per-pixel — drop a stale selection.
                      ...f, pixelId: e.target.value, optimizationEvent: '',
                    }))}
                  >
                    <option value="">— pick a pixel —</option>
                    {pixels.map((p) => (
                      <option key={p.pixelId} value={p.pixelId}>
                        {p.pixelName} ({p.events.length} {p.events.length === 1 ? 'event' : 'events'})
                      </option>
                    ))}
                  </select>
                )}
              </FormField>

              <FormField label="Optimisation event" required hintBelow hint="Only events already configured on the pixel can be optimised for.">
                <select
                  className={inputCls}
                  value={form.optimizationEvent}
                  onChange={(e) => setForm((f) => ({ ...f, optimizationEvent: e.target.value }))}
                  disabled={!selectedPixel || pixelHasNoEvents}
                >
                  <option value="">{selectedPixel ? '— pick an event —' : '— pick a pixel first —'}</option>
                  {(selectedPixel?.events ?? []).map((ev) => (
                    <option key={ev.eventType} value={ev.eventType}>{ev.label ?? ev.eventType}</option>
                  ))}
                </select>
              </FormField>

              {pixelHasNoEvents && (
                <div className="sm:col-span-2 -mt-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-2xs text-warning">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  {NO_EVENTS_WARNING}
                </div>
              )}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Budget mode" required>
              <select
                className={inputCls}
                value={form.budgetMode}
                onChange={(e) => setForm((f) => ({ ...f, budgetMode: e.target.value as AdGroupBudgetMode }))}
              >
                {ADGROUP_BUDGET_MODES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </FormField>

            <FormField label="Budget" required hintBelow hint="In your advertiser currency.">
              <input
                className={inputCls}
                type="number"
                min={0}
                step="any"
                placeholder={form.budgetMode === 'BUDGET_MODE_DAY' ? '20' : '500'}
                value={form.budget}
                onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
              />
            </FormField>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <FormField label="Schedule" required>
              <select
                className={inputCls}
                value={form.scheduleType}
                onChange={(e) => setForm((f) => ({ ...f, scheduleType: e.target.value as ScheduleType }))}
              >
                <option value="SCHEDULE_FROM_NOW">Run continuously</option>
                <option value="SCHEDULE_START_END">Start + end</option>
              </select>
            </FormField>

            <FormField label="Start time" required>
              <input
                type="datetime-local"
                className={inputCls}
                value={form.scheduleStartTime}
                onChange={(e) => setForm((f) => ({ ...f, scheduleStartTime: e.target.value }))}
              />
            </FormField>

            <FormField label="End time" hintBelow hint={form.scheduleType === 'SCHEDULE_FROM_NOW' ? 'Disabled — runs continuously.' : 'Required.'}>
              <input
                type="datetime-local"
                className={clsx(inputCls, form.scheduleType === 'SCHEDULE_FROM_NOW' && 'opacity-50')}
                disabled={form.scheduleType === 'SCHEDULE_FROM_NOW'}
                value={form.scheduleEndTime}
                onChange={(e) => setForm((f) => ({ ...f, scheduleEndTime: e.target.value }))}
              />
            </FormField>
          </div>

          <FormField label="Start status">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, operationStatus: 'DISABLE' }))}
                className={clsx(
                  'flex-1 rounded-xl border px-3 py-2 text-sm transition-colors',
                  form.operationStatus === 'DISABLE'
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border bg-surface text-text-primary hover:border-primary/40',
                )}
              >Paused (recommended)</button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, operationStatus: 'ENABLE' }))}
                className={clsx(
                  'flex-1 rounded-xl border px-3 py-2 text-sm transition-colors',
                  form.operationStatus === 'ENABLE'
                    ? 'border-success bg-success-soft text-success'
                    : 'border-border bg-surface text-text-primary hover:border-success/40',
                )}
              >Active immediately</button>
            </div>
          </FormField>

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <Button variant="ghost" onClick={() => { resetForm(); setCreateOpen(false); }} disabled={creating}>Cancel</Button>
            <Button variant="primary" onClick={submitCreate} loading={creating}>Create ad group</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

const BUDGET_MODE_LABEL: Record<string, string> = {
  BUDGET_MODE_DAY: '/day',
  BUDGET_MODE_TOTAL: 'lifetime',
  BUDGET_MODE_INFINITE: 'no cap',
};
